// Pure helpers for envelope sizing suggestions and rollover math.
// Envelope limits remain a flat {[key]: number} map in Firebase
// (legacy-compatible). Rollover flags live alongside as
// users/{uid}/envelopeMeta/{key} = { rollover: bool }.

import { isSpendingType, spendDelta } from '../txType.js';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// Parse a transaction's date so date-only ISO strings ("2026-06-01")
// are treated as LOCAL midnight rather than UTC. Matches the app's
// toLocalISO convention so bucketing doesn't drift across timezones.
function txDate(t) {
  if (t.date instanceof Date) return t.date;
  if (typeof t.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(t.date)) {
    return new Date(`${t.date}T00:00:00`);
  }
  return new Date(t.date);
}

function roundUpTo(value, step = 5) {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(value / step) * step;
}

function percentile(sortedAsc, p) {
  if (!sortedAsc.length) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const idx = (sortedAsc.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  const frac = idx - lo;
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * frac;
}

// Returns { [category]: number } — total expense spend in that category
// for transactions in the given calendar month.
// Bucketing uses LOCAL getMonth/getFullYear to match how the rest of the
// app determines a transaction's month (App.jsx and dataAdapters do the
// same), and to avoid timezone drift for ISO date-only strings.
export function spendByCategoryForMonth(transactions, monthStart) {
  const start = startOfMonth(monthStart);
  const targetYear = start.getFullYear();
  const targetMonth = start.getMonth();
  const out = {};
  transactions.forEach((t) => {
    // Refunds are in the spending lens as a credit — a returned purchase has
    // to give its envelope back.
    if (!isSpendingType(t.type)) return;
    const d = txDate(t);
    if (d.getFullYear() !== targetYear || d.getMonth() !== targetMonth) return;
    out[t.category] = (out[t.category] || 0) + spendDelta(t);
  });
  return out;
}

// Returns a suggestion per category looking back N COMPLETE prior months
// (excluding the current month). Result shape:
//   { [category]: { suggested, average, p90, sampleSize, months } }
// Categories with zero spending in the window are omitted.
export function suggestEnvelopeLimits(
  transactions,
  categories,
  options = {},
) {
  const { lookbackMonths = 3, today = new Date(), buffer = 1.15, roundTo = 5 } = options;

  const currentMonth = startOfMonth(today);
  const monthBuckets = [];
  for (let i = 1; i <= lookbackMonths; i++) {
    monthBuckets.push(addMonths(currentMonth, -i));
  }

  // Map category -> [monthlySpend, monthlySpend, ...]
  const seriesByCat = {};
  categories.forEach((c) => { seriesByCat[c] = []; });

  monthBuckets.forEach((m) => {
    const monthSpend = spendByCategoryForMonth(transactions, m);
    categories.forEach((c) => {
      seriesByCat[c].push(monthSpend[c] || 0);
    });
  });

  const out = {};
  categories.forEach((c) => {
    const series = seriesByCat[c];
    const total = series.reduce((s, v) => s + v, 0);
    if (total === 0) return;
    const nonZero = series.filter((v) => v > 0);
    const average = total / series.length;
    const sorted = [...series].sort((a, b) => a - b);
    const p90 = percentile(sorted, 0.9);
    const raw = Math.max(average * (Number(buffer) || 1), p90);
    out[c] = {
      suggested: roundUpTo(raw, roundTo),
      average: parseFloat(average.toFixed(2)),
      p90: parseFloat(p90.toFixed(2)),
      sampleSize: nonZero.length,
      months: series.length,
    };
  });

  return out;
}

// Computes effective spend / limit for an envelope row with optional
// rollover from the prior month's unused budget.
// Input:
//   { limit, prevLimit, prevSpent, rolloverEnabled }
// Output:
//   { effectiveLimit, rolloverBonus }
export function applyRollover({ limit, prevLimit, prevSpent, rolloverEnabled }) {
  const baseLimit = Number(limit) || 0;
  if (!rolloverEnabled) return { effectiveLimit: baseLimit, rolloverBonus: 0 };
  const pl = Number(prevLimit) || 0;
  const ps = Number(prevSpent) || 0;
  const bonus = Math.max(0, pl - ps);
  return { effectiveLimit: baseLimit + bonus, rolloverBonus: bonus };
}

export const _internals = { startOfMonth, addMonths, monthKey, MS_PER_DAY };
